import React, { useState } from 'react';
import { lazy, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
	GetSignoutResponse,
	PostErrorResponse,
	postSignout,
	PostSignoutRequest,
} from 'services/idm';

import { useBrowsing } from 'hooks/use-browsing';
import { useAppTranslation } from 'hooks/use-translates';

import { FormBox } from '@login/components/form-box';
import { FormHeader } from '@login/components/form-header';
import { apiErrorHandler } from '@login/helpers';

import styles from './signout-form.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));

type SignoutFormProps = {
	data: GetSignoutResponse | null;
	logoutId: string | null;
	returnUrl: string;
};

export const SignoutForm = ({
	data,
	logoutId,
	returnUrl,
}: SignoutFormProps) => {
	const [signoutData, setSignoutData] = useState(data);
	const { i18n } = useTranslation();
	const { push } = useBrowsing();
	const { t } = useAppTranslation(signoutData?.placeholders);
	const { handleSubmit } = useForm<PostSignoutRequest>();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const handleOnSubmit = async (formData: { returnUrl: string }) => {
		try {
			setIsSubmitting(true);
			const response = await postSignout({
				data: {
					returnUrl: formData.returnUrl || returnUrl,
					logoutId: logoutId ?? '',
				},
				lang: i18n.language,
				token: signoutData?.xsrfToken,
			});

			if (response.action === 'Display') {
				setSignoutData(response);
			}

			if (response.action === 'Redirect' && response.redirectUrl) {
				if (response.redirectUrl.startsWith('/connect')) {
					const absoluteUrl = `${window.location.origin}${response.redirectUrl}`;
					window.location.href = absoluteUrl;
				} else {
					push(response.redirectUrl);
				}
			} else {
				setIsSubmitting(false);
			}
		} catch (e) {
			const error = e as PostErrorResponse;
			apiErrorHandler(error, i18n.language);
			setIsSubmitting(false);
			if ((error as PostErrorResponse).title === 'INVALID_LOGOUT') {
				console.error('Invalid logout request:', error);
			}
		}
	};

	return (
		<Suspense fallback={null}>
			<form onSubmit={handleSubmit(handleOnSubmit)} id="form-profile">
				<FormBox>
					<FormHeader
						headerTitle={
							signoutData?.showLogoutPrompt
								? t('signout.signout_heading')
								: t('signout.signedout_heading')
						}
					/>

					{signoutData?.showLogoutPrompt && (
						<div className={styles['form-box-content']}>
							<p className="body-medium bold">
								{t('signout.signout_message')}
							</p>
							<Button
								type="submit"
								label={t('signout.signout_button')}
								isLoading={isSubmitting}
								disabled={isSubmitting}
								variant="primary"
								fullWidth={true}
							/>
						</div>
					)}

					{!signoutData?.showLogoutPrompt && (
						<div className={styles['form-box-content']}>
							<p className="body-large bold">
								{t('signout.signedout_message')}
							</p>

							<p
								className={`body-small ${styles.redirectBackText}`}
							>
								<Trans
									i18nKey={
										!signoutData?.automaticRedirectAfterSignOut
											? `${t(
													'signout.return_message_with_redirect',
													{
														redirectUrl:
															signoutData?.postLogoutRedirectUri ||
															'',
													}
												)}`
											: `${t(
													'signout.return_message_no_redirect',
													{
														redirectUrl:
															signoutData?.postLogoutRedirectUri ||
															'',
													}
												)}`
									}
									components={{
										a: (
											<Button
												key="0"
												type="button"
												variant="link"
												className={
													styles['redirect-back']
												}
											/>
										),
									}}
								/>
							</p>
						</div>
					)}
				</FormBox>
			</form>
		</Suspense>
	);
};

export default SignoutForm;
